import React, { useEffect, useState } from "react";
import {
  Chart,
  ChartSeries,
  ChartTooltip,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTitle,
  ChartLegend,
  ChartArea,
  ChartSeriesDefaults,
} from "@progress/kendo-react-charts";
import { useTranslation } from "react-i18next";

const getAmpHoursColor = (value, averageAmpHoursDetails) => {
  if (!averageAmpHoursDetails) return "green";
  const outOfRange =
    value > averageAmpHoursDetails.RangeHigh ||
    value < averageAmpHoursDetails.RangeLow ||
    (averageAmpHoursDetails.RangeLow === 0 && value === 0);
  return outOfRange ? "red" : "green";
};

const getTooltipStyle = (backgroundColor) => ({
  border: "1px solid #fff",
  borderRadius: "3px",
  color: "white",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  minWidth: "165px",
  minHeight: "33px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: backgroundColor,
});

const SharedTooltip = ({ points, averageAmpHoursDetails, t }) => {
  if (!points || !points[0]?.dataItem) return null;

  const backgroundColor = getAmpHoursColor(points[0].value, averageAmpHoursDetails);
  return (
    <div style={getTooltipStyle(backgroundColor)}>
      <div>
        Car {points[0]?.dataItem?.CarNumber} {t("Amp_Hours")}{" "}
        {points[0]?.dataItem?.AmpHours}
      </div>
    </div>
  );
};

const AmpHoursChart = ({ 
  data, 
  onRefresh, 
  onSeriesClick,
  onSeriesHover,
  title = "Amp_Hours",
  showTitle = true,
  includeZeros = false 
}) => {
  const { t } = useTranslation();
  const [ampHoursData, setAmpHoursData] = useState([]);
  const [ampHourAvgArrays, setAmpHourAvgArrays] = useState([]);
  const [chartState, setChartState] = useState({
    averageAmpHoursDetails: null,
    minAmpGraph: 0,
    maxAmpGraph: 0
  });

  // Statistical calculation functions
  function getSigmaOnArray(arrayToSigma) {
    if (!arrayToSigma || arrayToSigma.length === 0) return 0;
    var arrayLength = arrayToSigma.length;
    var interimArray = [];
    var initialArrayAverage = getAvgFromArray(arrayToSigma);
    for (var i = 0; i < arrayLength; i++) {
      interimArray.push(
        (arrayToSigma[i] - initialArrayAverage) *
          (arrayToSigma[i] - initialArrayAverage)
      );
    }
    var interimArrayAvergage = getAvgFromArray(interimArray);
    var arraySigma = Math.sqrt(interimArrayAvergage);
    return arraySigma;
  }

  function getAvgFromArray(arrayToAverage) {
    if (!arrayToAverage || arrayToAverage.length === 0) return 0;
    var arrayItemTotal = 0;
    var arrayLength = arrayToAverage.length;
    for (var i = 0; i < arrayLength; i++) {
      arrayItemTotal = arrayItemTotal + arrayToAverage[i];
    }
    return arrayItemTotal / arrayLength;
  }

  function roundDownNearestPow10(numberToRound) {
    if (numberToRound <= 0) return 0;
    var numDigits = Math.floor(Math.log10(numberToRound));
    var powerOf10 = Math.pow(10, numDigits);
    return Math.floor(numberToRound / powerOf10) * powerOf10;
  }

  function getMinFromArray(arrayToMin) {
    if (!arrayToMin || arrayToMin.length === 0) return 0;
    return Math.min(...arrayToMin);
  }

  function getMaxFromArray(arrayToMax) {
    if (!arrayToMax || arrayToMax.length === 0) return 0;
    return Math.max(...arrayToMax);
  }

  function getNumberOfDigits(number) {
    return Math.floor(Math.log10(Math.abs(number))) + 1;
  }

  function getMinMaxForChart(chartArray, avgFromArray, sigmaFromArray) {
    if (!chartArray || chartArray.length === 0) return { Min: 0, Max: 0 };
    
    var tempChartMinMax = {};
    var tempChartMin;
    var tempChartMax;

    //get min and max from array
    var arrayMax = getMaxFromArray(chartArray);
    var arrayMin = roundDownNearestPow10(getMinFromArray(chartArray));

    //get min and max based on 2 sigmas from avg
    var maxFromAvg = avgFromArray + (2 * sigmaFromArray);
    var minFromAvg = roundDownNearestPow10(avgFromArray - (2 * sigmaFromArray));

    //compare the two, use min from array if lower since it will include all cars, if same calculate min based on range
    if (arrayMin < minFromAvg) {
      tempChartMin = (arrayMin < 0) ? 0 : arrayMin;
    }
    else if (arrayMin === minFromAvg) {
      if ((maxFromAvg - minFromAvg) < 10) {  //really small range
        tempChartMin = (arrayMin <= 0) ? 0 : (arrayMin - 0.2);
      }
      else {
        var numDigs = (getNumberOfDigits(maxFromAvg - minFromAvg)) - 1;
        tempChartMin = (arrayMin <= 0) ? 0 : (arrayMin - (Math.pow(10, numDigs)));
      }
    }
    else {
      tempChartMin = (minFromAvg < 0) ? 0 : minFromAvg;
    }

    //set max based on min to keep avg line in the middle
    var diffBetweenAvgAndMin = avgFromArray - tempChartMin;
    tempChartMax = avgFromArray + diffBetweenAvgAndMin;

    tempChartMinMax.Min = tempChartMin;
    tempChartMinMax.Max = tempChartMax;

    return tempChartMinMax;
  }

  // Process data when input changes
  useEffect(() => {
    // Handle both data structures: direct array or object with CarUsage array
    let carData = [];
    if (data && Array.isArray(data)) {
      carData = data;
    } else if (data && data.CarUsage && Array.isArray(data.CarUsage)) {
      carData = data.CarUsage;
    }

    if (carData && carData.length > 0) {
      let ampHoursArray = [];
      let formattedAmpHoursData = [];

      // Process data for chart
      carData.forEach((el, i) => {
        const ampHours = el.AmpHours || 0;
        if (includeZeros || ampHours !== 0) {
          ampHoursArray.push(ampHours);
          formattedAmpHoursData.push({
            CarNumber: el.CarNumber || `Car ${i + 1}`,
            AmpHours: ampHours
          });
        }
      });

      if (ampHoursArray.length === 0) {
        setAmpHoursData([]);
        setAmpHourAvgArrays([]);
        setChartState({
          averageAmpHoursDetails: null,
          minAmpGraph: 0,
          maxAmpGraph: 0
        });
        return;
      }

      var sigmaAmpHours = Math.round(getSigmaOnArray(ampHoursArray));
      var averageAmpHours = Math.round(getAvgFromArray(ampHoursArray));
      var ampHoursMinMax = getMinMaxForChart(
        ampHoursArray,
        averageAmpHours,
        sigmaAmpHours
      );

      var rangeLowAmpHours = null;
      var rangeHighAmpHours = averageAmpHours + sigmaAmpHours;

      if (averageAmpHours - sigmaAmpHours < 0) {
        rangeLowAmpHours = 0;
      } else {
        rangeLowAmpHours = averageAmpHours - sigmaAmpHours;
      }

      // Create average line data
      let avgData = formattedAmpHoursData.map((item) => ({
        CarNumber: item.CarNumber,
        AmpHours: averageAmpHours
      }));

      // Create usage detail state
      const averageAmpHoursDetails = {
        Average: averageAmpHours,
        RangeLow: rangeLowAmpHours,
        RangeHigh: rangeHighAmpHours
      };

      setAmpHoursData(formattedAmpHoursData);
      setAmpHourAvgArrays(avgData);
      setChartState({
        averageAmpHoursDetails,
        minAmpGraph: ampHoursMinMax.Min,
        maxAmpGraph: ampHoursMinMax.Max
      });
    }
  }, [data, includeZeros]);

  const handleAmpHoursColor = (e) =>
    getAmpHoursColor(e.value, chartState.averageAmpHoursDetails);

  const sharedTooltipRender = (context) => (
    <SharedTooltip
      {...context}
      t={t}
      averageAmpHoursDetails={chartState.averageAmpHoursDetails}
    />
  );

  // Check if we have data or if the original data structure has AverageAmpHoursDetails
  const hasData = chartState.averageAmpHoursDetails || 
    (data && data.AverageAmpHoursDetails) ||
    (data && Array.isArray(data) && data.length > 0) ||
    (data && data.CarUsage && data.CarUsage.length > 0);

  if (!hasData) {
    return (
      <div>
        <strong>{t(title)}</strong>
        <br />
        {t("No_data")}
      </div>
    );
  }

  return (
    <Chart onRefresh={onRefresh} onSeriesClick={onSeriesClick} onSeriesHover={onSeriesHover}>
      <ChartArea background="#fff" margin={0} />
      <ChartLegend visible={false} />
      {showTitle && <ChartTitle text={t(title)} font="14pt sans-serif" />}
      <ChartTooltip
        border={{ color: "white" }}
        background="transparent"
        color="white"
        shared={true}
        render={sharedTooltipRender}
        visible={true}
      />
      <ChartSeriesDefaults type="column" line={{ width: 1 }} />
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          axisCrossingValue={0}
          minorTicks={{ visible: false }}
          labels={{ visible: false }}
          field="CarNumber"
        />
      </ChartCategoryAxis>
      <ChartValueAxis>
        <ChartValueAxisItem
          axisCrossingValue={0}
          labels={{
            color: "black",
            format: "{0}",
          }}
          plotBands={chartState.averageAmpHoursDetails ? [
            {
              from: chartState.averageAmpHoursDetails.RangeHigh,
              to: 1000000,
              color: "red",
              opacity: 0.1,
            },
            {
              from: chartState.averageAmpHoursDetails.RangeLow,
              to: -1000000,
              color: "red",
              opacity: 0.1,
            },
            {
              from: chartState.averageAmpHoursDetails.Average,
              to: chartState.averageAmpHoursDetails.RangeHigh,
              color: "green",
              opacity: 0.2,
            },
            {
              from: chartState.averageAmpHoursDetails.Average,
              to: chartState.averageAmpHoursDetails.RangeLow,
              color: "green",
              opacity: 0.2,
            },
          ] : []}
          min={chartState.minAmpGraph}
          max={chartState.maxAmpGraph}
          minorTicks={{ visible: false }}
        />
      </ChartValueAxis>
      <ChartSeries>
        <ChartSeriesItem
          gap={0}
          line={1}
          field="AmpHours"
          name="AmpHours"
          color={(e) => handleAmpHoursColor(e)}
          data={ampHoursData.sort((a, b) => a.AmpHours - b.AmpHours)}
        />
        <ChartSeriesItem
          type="line"
          style="smooth"
          name="Average"
          color="black"
          markers={{
            visible: false,
          }}
          data={ampHourAvgArrays}
        />
      </ChartSeries>
    </Chart>
  );
};

export { AmpHoursChart };